import React, { useState, useEffect } from 'react';
import './CustomizeTaskListPage.css';
import { PageHeader } from '../../components/PageHeader/PageHeader';
import type { DaySchedule, Task } from '../../types';
import { scheduleData } from '../schedule/scheduleData';

interface CustomizeTaskListPageProps {
  customSchedule: DaySchedule[] | null;
  onSave: (schedule: DaySchedule[]) => void;
  onNavigateBack: () => void;
  showToast: (msg: string) => void;
}

export const CustomizeTaskListPage: React.FC<CustomizeTaskListPageProps> = ({
  customSchedule, onSave, onNavigateBack, showToast,
}) => {
  const [editSchedule, setEditSchedule] = useState<DaySchedule[]>(
    () => JSON.parse(JSON.stringify(customSchedule || scheduleData))
  );
  const [hasChanges, setHasChanges] = useState(false);

  const handleSave = () => {
    onSave(editSchedule);
    setHasChanges(false);
    showToast('Schedule saved');
  };

  const handleResetAll = () => {
    setEditSchedule(JSON.parse(JSON.stringify(scheduleData)));
    setHasChanges(true);
  };

  const updateTask = (dayIndex: number, taskIndex: number, field: keyof Task, value: string | number | undefined) => {
    setEditSchedule((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      (next[dayIndex].tasks[taskIndex] as any)[field] = value;
      return next;
    });
    setHasChanges(true);
  };

  const addTask = (dayIndex: number) => {
    setEditSchedule((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const prefix = next[dayIndex].day.slice(0, 3).toLowerCase();
      next[dayIndex].tasks.push({
        id: `${prefix}-${Date.now()}`,
        label: '',
      });
      return next;
    });
    setHasChanges(true);
  };

  const removeTask = (dayIndex: number, taskIndex: number) => {
    setEditSchedule((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next[dayIndex].tasks.splice(taskIndex, 1);
      return next;
    });
    setHasChanges(true);
  };

  const updateTheme = (dayIndex: number, theme: string) => {
    setEditSchedule((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next[dayIndex].theme = theme;
      return next;
    });
    setHasChanges(true);
  };

  return (
    <div className="customize-page">
      <PageHeader
        title="Customize Schedule"
        leftAction={
          <button className="cust-back-btn" onClick={onNavigateBack}>
            <svg viewBox="0 0 24 24" width="20" height="20">
              <polyline points="15 18 9 12 15 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        }
        rightAction={
          hasChanges ? (
            <button className="cust-save-btn" onClick={handleSave}>Save</button>
          ) : <div style={{ width: 36 }} />
        }
      />

      <div className="cust-content">
        {editSchedule.map((day, dayIdx) => (
          <div key={day.day} className="cust-day" style={{ '--cust-color': day.color } as React.CSSProperties}>
            <div className="cust-day-header">
              <h3 className="cust-day-name">{day.day}</h3>
              <input
                className="cust-theme-input"
                value={day.theme}
                onChange={(e) => updateTheme(dayIdx, e.target.value)}
                placeholder="Theme name"
              />
            </div>

            <div className="cust-tasks">
              {day.tasks.map((task, taskIdx) => (
                <div key={task.id} className="cust-task-row">
                  <input
                    className="cust-task-input"
                    value={task.label}
                    onChange={(e) => updateTask(dayIdx, taskIdx, 'label', e.target.value)}
                    placeholder="Task name"
                  />
                  <div className="cust-task-actions">
                    <label className="cust-week-toggle">
                      <span className="cust-week-label">Wk</span>
                      <select
                        className="cust-week-select"
                        value={task.weekNum ?? ''}
                        onChange={(e) => updateTask(dayIdx, taskIdx, 'weekNum', e.target.value ? Number(e.target.value) : undefined)}
                      >
                        <option value="">All</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </select>
                    </label>
                    <button className="cust-remove-btn" onClick={() => removeTask(dayIdx, taskIdx)} aria-label="Remove">
                      <svg viewBox="0 0 16 16" width="14" height="14">
                        <line x1="4" y1="4" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <line x1="12" y1="4" x2="4" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <button className="cust-add-task" onClick={() => addTask(dayIdx)}>
                + Add Task
              </button>
            </div>
          </div>
        ))}

        <button className="cust-reset-all" onClick={handleResetAll}>
          Reset to Defaults
        </button>
      </div>
    </div>
  );
};
